import { type MouseEvent, memo, useCallback } from 'react';

import { Pause, PlayFilled } from '@air/next-icons';
import { IconButton, type IconButtonProps } from '@air/primitive-icon-button';

import { usePlayerPlayPause } from '../hooks/usePlayerPlayPause';
import { usePlayerStore } from './PlayerContextProvider';

export type PlayerPlayButtonProps = Omit<IconButtonProps, 'children' | 'label'>;

export const PlayerPlayButton = memo(({ onClick, ...restOfProps }: PlayerPlayButtonProps) => {
  const playing = usePlayerStore((state) => state.playing);
  const videoElement = usePlayerStore((state) => state.videoElement);
  const { onPlayerPlayPause } = usePlayerPlayPause();

  const handleTogglePlay = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      onPlayerPlayPause();

      onClick?.(event);
    },
    [onClick, onPlayerPlayPause],
  );

  return (
    <IconButton
      appearance="ghost"
      color="grey"
      data-testid="PLAYER_PLAY_BUTTON"
      disabled={!videoElement}
      label="Play/Pause"
      onClick={handleTogglePlay}
      {...restOfProps}
    >
      {playing ? <Pause className="size-4" /> : <PlayFilled className="size-4" />}
    </IconButton>
  );
});

PlayerPlayButton.displayName = 'PlayerPlayButton';
